import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditClientProfileContent } from './content'
import { EditClientProfileControls } from './controls'

export enum Input {
  FirstName = 'first_name',
  LastName = 'last_name',
}

export const editClientProfileSchema = z.object({
  [Input.FirstName]: z.string()
    .min(1, { message: 'First name is required.' })
    .max(255, { message: 'First name must be less than 255 characters.' }),
  [Input.LastName]: z.string()
    .min(1, { message: 'Last name is required.' })
    .max(255, { message: 'Last name must be less than 255 characters.' }),
})

export type EditClientProfileSchema = z.infer<typeof editClientProfileSchema>

type EditClientProfileFormContextType = {
  onSubmit: (values: EditClientProfileSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditClientProfileFormContext = createContext<EditClientProfileFormContextType | undefined>(undefined)

type EditClientProfileFormProps = EditClientProfileFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditClientProfileSchema>,
}

export function EditClientProfileForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditClientProfileFormProps) {

  const context: EditClientProfileFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditClientProfileFormContext.Provider value={context}>
      <Form<EditClientProfileSchema, typeof editClientProfileSchema>
        schema={editClientProfileSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditClientProfileFormContext.Provider>
  )
}

export function useEditClientProfileForm() {
  const context = useContext(EditClientProfileFormContext)
  if (context === undefined) {
    throw new Error('useEditClientProfileForm must be used within an EditClientProfileFormProvider')
  }
  return context
}

// Compound components
EditClientProfileForm.Content = EditClientProfileContent
EditClientProfileForm.Controls = EditClientProfileControls
